import {Fragment, useEffect, useMemo, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {format} from 'date-fns';
import {Archive, ArrowLeft, Calendar, Loader2, Shield, Users,} from 'lucide-react';
import {v2Api} from '@/lib/api';
import type {ImmutableExerciseReport, ImmutableExerciseReportParticipant} from '@/lib/api/v2';
import {Button} from '@/components/ui/button';
import {Badge} from '@/components/ui/badge';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow,} from '@/components/ui/table';
import {Tabs, TabsList, TabsTrigger} from '@/components/ui/tabs';
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog';
import D3BarChart from '@/components/analytics/D3BarChart';
import D3PieChart, {type PieDatum} from '@/components/analytics/D3PieChart';
import ParticipantsTemplateExportButton from '@/components/scenarios/ParticipantsTemplateExportButton';
import * as XLSX from 'xlsx';

function frozenExerciseName(snapshot: Record<string, unknown>): string {
    const ex = snapshot.exercise;
    if (ex && typeof ex === 'object' && ex !== null && 'name' in ex) {
        const n = (ex as { name?: string }).name;
        if (typeof n === 'string' && n.trim()) return n;
    }
    return '—';
}

function getParticipantTeamId(p: {
    team: { id: number } | null | undefined;
}): number {
    return Number(p.team?.id ?? 0);
}

function formatScoreLine(obtained: number, outOf: number): string {
    return outOf > 0 ? `${obtained} / ${outOf}` : `${obtained}`;
}

function phaseScoreLine(phase?: {
    name: string;
    out_of?: number;
    obtained_score?: number;
    bonus_points?: number;
}): string {
    if (!phase) return '';
    const obtained = Number(phase.obtained_score ?? 0) + Number(phase.bonus_points ?? 0);
    const outOf = Number(phase.out_of ?? 0);
    const phaseName = typeof phase.name === 'string' && phase.name.trim() ? phase.name : 'Unassigned';
    return `${phaseName}: ${formatScoreLine(obtained, outOf)}`;
}

function renderFeedbackAnswer(value: unknown): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'string') return value.trim() || '—';
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return JSON.stringify(value);
}

function isQuestionAnswerItem(value: unknown): value is { id?: string; question?: string; answer?: unknown } {
    return typeof value === 'object' && value !== null;
}

export default function ExerciseImmutableArchiveReportPage() {
    const {archiveUuid} = useParams<{ archiveUuid: string }>();
    const [data, setData] = useState<ImmutableExerciseReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'participants' | 'injects' | 'flags' | 'feedback'>('overview');
    const [phaseDrawerOpen, setPhaseDrawerOpen] = useState(false);
    const [selectedParticipantKey, setSelectedParticipantKey] = useState<string | null>(null);
    const scenarioSnapshot = useMemo(
        () => (data?.scenario && typeof data.scenario === 'object' ? data.scenario : {}),
        [data],
    );

    const teamIdToName = useMemo(() => {
        const mappings = (scenarioSnapshot as any).team_mappings;
        const out = new Map<number, string>();
        if (!Array.isArray(mappings)) return out;
        for (const tm of mappings) {
            const tid = tm?.team_id;
            const tname = tm?.team_name;
            if (tid === undefined || tid === null) continue;
            const n = typeof tid === 'number' ? tid : Number(tid);
            if (!Number.isFinite(n)) continue;
            out.set(n, typeof tname === 'string' ? tname : '');
        }
        return out;
    }, [scenarioSnapshot]);

    const participantKeyToName = useMemo(() => {
        const m = new Map<string, string>();
        if (!data) return m;
        for (const p of data.participants) {
            const teamId = getParticipantTeamId(p);
            const teamName = teamIdToName.get(teamId) || p.team?.name || '';
            const base = p.full_name || p.email || `User ${p.user_id}`;
            m.set(`${p.user_id}:${teamId}`, teamName ? `${base} (${teamName})` : base);
        }
        return m;
    }, [data, teamIdToName]);

    const participantKeyToPersonName = useMemo(() => {
        const m = new Map<string, string>();
        if (!data) return m;
        for (const p of data.participants) {
            const base = p.full_name || p.email || `User ${p.user_id}`;
            m.set(`${p.user_id}:${getParticipantTeamId(p)}`, base);
        }
        return m;
    }, [data]);

    const deliveryRows = useMemo(() => {
        if (!data) return [];
        const flagMappings = (scenarioSnapshot as any).flag_mappings ?? data.flags ?? null;
        const teamMappings = (scenarioSnapshot as any).team_mappings ?? null;

        // `ExerciseFlagMapModel` stores `exercise_team_id`; we need to convert that to the real `team_id`
        // so we can match against archived `delivery.role_id` (which is the team id).
        const exerciseTeamIdToTeamId = new Map<string, number>();
        // `ExerciseTeamMapModel.vm_required` tells us whether that exercise team belongs to VM/console
        // (Scenario) flow vs TTX-first flow.
        const exerciseTeamIdToVmRequired = new Map<string, boolean>();
        if (Array.isArray(teamMappings)) {
            for (const tm of teamMappings) {
                const exTeamId = tm?.id ?? tm?.exercise_team_id;
                const teamId = tm?.team_id ?? tm?.teamId;
                if (exTeamId === null || exTeamId === undefined) continue;
                if (teamId === null || teamId === undefined) continue;
                const exTeamIdStr = String(exTeamId);
                const teamIdNum = Number(teamId);
                if (!Number.isFinite(teamIdNum)) continue;
                exerciseTeamIdToTeamId.set(exTeamIdStr, teamIdNum);
                const vmRequiredVal = tm?.vm_required ?? tm?.vmRequired;
                exerciseTeamIdToVmRequired.set(exTeamIdStr, Boolean(vmRequiredVal));
            }
        }

        // Split denominators by flag->team classification:
        // - Scenario: exercise_team.vm_required === true
        // - TTX: exercise_team.vm_required === false
        const flagScenarioOutOfByQuestionTeam = new Map<string, number>();
        const flagTtxOutOfByQuestionTeam = new Map<string, number>();
        if (Array.isArray(flagMappings)) {
            for (const fm of flagMappings) {
                const flag = fm?.flag ?? {};
                const q = typeof flag.question === 'string' ? flag.question.trim().toLowerCase() : '';
                const exTeamId = fm?.exercise_team_id ?? fm?.exerciseTeamId;
                const teamIdFromMap =
                    exTeamId !== null && exTeamId !== undefined
                        ? exerciseTeamIdToTeamId.get(String(exTeamId))
                        : undefined;
                const teamId = teamIdFromMap ?? flag.team_id ?? flag.teamId ?? null;
                if (!q || teamId === null || teamId === undefined) continue;
                const score = Number(flag.score ?? 0) || 0;
                const vmRequired = exTeamId !== null && exTeamId !== undefined ? exerciseTeamIdToVmRequired.get(String(exTeamId)) : undefined;
                const isScenarioFlag = vmRequired === true;
                const key = `${q}|${teamId}`;
                if (isScenarioFlag) {
                    flagScenarioOutOfByQuestionTeam.set(key, score);
                } else {
                    flagTtxOutOfByQuestionTeam.set(key, score);
                }
            }
        }
        const useFlagScoring =
            flagScenarioOutOfByQuestionTeam.size > 0 || flagTtxOutOfByQuestionTeam.size > 0;

        return data.inject_deliveries.map((d) => {
            const hintTimes = (d.hint_used_at_list ?? []).filter(Boolean);
            const hintTimesJoined = hintTimes.length ? hintTimes.join(', ') : '';
            const responded =
                Boolean(d.responded_at) ||
                Boolean((d.response_text ?? '').trim()) ||
                ((d.selected_option_ids ?? []).length > 0);
            const rawAttemptCount = Number(d.attempt_count ?? 0) || 0;
            const inferredAttemptCount = rawAttemptCount > 0 ? rawAttemptCount : responded ? 1 : 0;
            const scoreSum = Number(d.obtained_score ?? 0) + Number(d.bonus_points ?? 0);
            const inferredIsCorrect = scoreSum > 0 ? true : Boolean(d.is_correct ?? false);

            const phaseNames = (d.inject_phases_snapshot ?? [])
                .map((x) => x?.name)
                .filter((n): n is string => typeof n === 'string' && n.trim().length > 0);
            const phasesOut = phaseNames.length ? phaseNames : ['Unassigned'];

            const questionKey = `${(d.inject_title ?? '').trim().toLowerCase()}|${d.role_id}`;
            const matchedScenarioOutOf = flagScenarioOutOfByQuestionTeam.get(questionKey);
            const matchedTtxOutOf = flagTtxOutOfByQuestionTeam.get(questionKey);

            // When we have flag-based denominators, only count deliveries that matched the corresponding bucket.
            // This prevents TTX points from leaking into Scenario Score for `combined` participants.
            const counted_for_scenario_flags = useFlagScoring ? matchedScenarioOutOf !== undefined : true;
            const counted_for_ttx_flags = useFlagScoring ? matchedTtxOutOf !== undefined : true;

            // Prefer flag score (denominator) when we can match the delivery to a flag.
            // Fallback to inject_points if the match fails (e.g., title mismatch).
            const flag_scenario_out_of = useFlagScoring
                ? (matchedScenarioOutOf ?? Number(d.inject_points ?? 0) ?? 0)
                : Number(d.inject_points ?? 0) ?? 0;
            const flag_ttx_out_of = useFlagScoring
                ? (matchedTtxOutOf ?? Number(d.inject_points ?? 0) ?? 0)
                : Number(d.inject_points ?? 0) ?? 0;

            return {
                inject_title: d.inject_title || `Inject #${d.inject_id}`,
                inject_id: d.inject_id,
                inject_points: d.inject_points,
                inject_points_type: d.inject_points_type,
                user_id: d.user_id,
                participant_name: participantKeyToName.get(`${d.user_id}:${d.role_id}`) || `User ${d.user_id}`,
                role_id: d.role_id,
                team: {id: d.role_id, name: teamIdToName.get(d.role_id) || ''},
                attempt_count: inferredAttemptCount,
                is_correct: inferredIsCorrect,
                hint_used: d.hint_used ?? false,
                hint_used_at_list: hintTimesJoined,
                sent_at: d.sent_at ?? '',
                responded_at: d.responded_at ?? '',
                obtained_score: d.obtained_score,
                bonus_points: d.bonus_points,
                response_text: d.response_text ?? '',
                selected_option_ids: d.selected_option_ids ?? [],
                remarks: (d as any).remarks ?? '',
                evaluated_by_id: d.evaluated_by_id ?? '',
                phase_names: phasesOut,
                // Scenario vs TTX buckets (used for numerator + denominator splitting).
                counted_for_scenario_flags,
                counted_for_ttx_flags,
                flag_scenario_out_of,
                flag_ttx_out_of,
            };
        });
    }, [scenarioSnapshot, data, participantKeyToName, teamIdToName]);

    const teamScores = useMemo(() => data?.team_scores ?? [], [data]);
    const summary = useMemo(() => {
        if (!data) return null;
        if (data.summary) return data.summary;
        const ttxObtained = (data.participants ?? []).reduce(
            (acc, p) => acc + Number(p.score?.ttx?.obtained?.obtained_score ?? 0) + Number(p.score?.ttx?.obtained?.bonus_points ?? 0),
            0,
        );
        const ttxOutOf = (data.participants ?? []).reduce((acc, p) => acc + Number(p.score?.ttx?.out_of ?? 0), 0);
        const scObtained = (data.participants ?? []).reduce(
            (acc, p) => acc + Number(p.score?.scenarios?.obtained?.obtained_score ?? 0) + Number(p.score?.scenarios?.obtained?.bonus_points ?? 0),
            0,
        );
        const scOutOf = (data.participants ?? []).reduce((acc, p) => acc + Number(p.score?.scenarios?.out_of ?? 0), 0);
        const grandObtained = ttxObtained + scObtained;
        const grandOutOf = ttxOutOf + scOutOf;
        return {
            participants_count: data.participants.length,
            teams_count: teamScores.length,
            ttx: {out_of: ttxOutOf, per: ttxOutOf > 0 ? Number(((ttxObtained / ttxOutOf) * 100).toFixed(2)) : 0, obtained: {obtained_score: ttxObtained, bonus_points: 0}},
            scenarios: {out_of: scOutOf, per: scOutOf > 0 ? Number(((scObtained / scOutOf) * 100).toFixed(2)) : 0, obtained: {obtained_score: scObtained, bonus_points: 0}},
            grand_total: {out_of: grandOutOf, per: grandOutOf > 0 ? Number(((grandObtained / grandOutOf) * 100).toFixed(2)) : 0, obtained: {obtained_score: grandObtained, bonus_points: 0}},
        };
    }, [data, teamScores.length]);
    const teamPerformanceChart = useMemo(() => {
        const rows = (teamScores ?? []).map((t) => {
            const g = t.score?.grand_total;
            if (g) {
                const obtained = Number(g.obtained?.obtained_score ?? 0) + Number(g.obtained?.bonus_points ?? 0);
                const outOf = Number(g.out_of ?? 0);
                const per = Number(g.per ?? (outOf > 0 ? ((obtained / outOf) * 100).toFixed(2) : 0)) || 0;
                return {label: t.team?.name || '—', per};
            }
            const ttxObtained = Number(t.score?.ttx?.obtained?.obtained_score ?? 0) + Number(t.score?.ttx?.obtained?.bonus_points ?? 0);
            const ttxOut = Number(t.score?.ttx?.out_of ?? 0);
            const scObtained = Number(t.score?.scenarios?.obtained?.obtained_score ?? 0) + Number(t.score?.scenarios?.obtained?.bonus_points ?? 0);
            const scOut = Number(t.score?.scenarios?.out_of ?? 0);
            const obtained = ttxObtained + scObtained;
            const outOf = ttxOut + scOut;
            return {label: t.team?.name || '—', per: outOf > 0 ? Number(((obtained / outOf) * 100).toFixed(2)) : 0};
        });
        return {
            labels: rows.map((r) => r.label),
            values: rows.map((r) => r.per),
        };
    }, [teamScores]);

    const participantTtxScoreMap = useMemo(() => {
        const accessMode = new Map<string, string>();
        for (const p of data?.participants ?? []) {
            accessMode.set(`${p.user_id}:${getParticipantTeamId(p)}`, p.access_mode);
        }

        const out = new Map<string, number>();
        for (const r of deliveryRows) {
            const key = `${r.user_id}:${r.role_id}`;
            const am = accessMode.get(key) ?? '';
            const isTtx = am === 'ttx_only' || am === 'combined';
            if (!isTtx) continue;

            const include = Boolean((r as any).counted_for_ttx_flags);
            if (!include) continue;

            const rowScore = Number(r.obtained_score || 0) + Number(r.bonus_points || 0);
            out.set(key, (out.get(key) || 0) + rowScore);
        }
        return out;
    }, [data, deliveryRows]);

    const participantScenarioScoreMap = useMemo(() => {
        const accessMode = new Map<string, string>();
        for (const p of data?.participants ?? []) {
            accessMode.set(`${p.user_id}:${getParticipantTeamId(p)}`, p.access_mode);
        }

        const out = new Map<string, number>();
        for (const r of deliveryRows) {
            const key = `${r.user_id}:${r.role_id}`;
            const am = accessMode.get(key) ?? '';
            const isScenario = am === 'vm_only' || am === 'combined';
            if (!isScenario) continue;

            const include = Boolean((r as any).counted_for_scenario_flags);
            if (!include) continue;

            const rowScore = Number(r.obtained_score || 0) + Number(r.bonus_points || 0);
            out.set(key, (out.get(key) || 0) + rowScore);
        }
        return out;
    }, [data, deliveryRows]);

    const participantAccessModeMap = useMemo(() => {
        const out = new Map<string, string>();
        for (const p of data?.participants ?? []) {
            out.set(`${p.user_id}:${getParticipantTeamId(p)}`, p.access_mode);
        }
        return out;
    }, [data]);

    const participantTtxOutOfMap = useMemo(() => {
        const out = new Map<string, number>();
        for (const r of deliveryRows) {
            const key = `${r.user_id}:${r.role_id}`;
            const am = participantAccessModeMap.get(key) ?? '';
            const isTtx = am === 'ttx_only' || am === 'combined';
            if (!isTtx) continue;
            const include = Boolean((r as any).counted_for_ttx_flags);
            if (!include) continue;
            const outOf = Number((r as any).flag_ttx_out_of ?? 0) || 0;
            out.set(key, (out.get(key) || 0) + outOf);
        }
        return out;
    }, [deliveryRows, participantAccessModeMap]);

    const participantScenarioOutOfMap = useMemo(() => {
        const out = new Map<string, number>();
        for (const r of deliveryRows) {
            const key = `${r.user_id}:${r.role_id}`;
            const am = participantAccessModeMap.get(key) ?? '';
            const isScenario = am === 'vm_only' || am === 'combined';
            if (!isScenario) continue;
            const include = Boolean((r as any).counted_for_scenario_flags);
            if (!include) continue;
            const outOf = Number((r as any).flag_scenario_out_of ?? 0) || 0;
            out.set(key, (out.get(key) || 0) + outOf);
        }
        return out;
    }, [deliveryRows, participantAccessModeMap]);

    const selectedParticipant = useMemo(() => {
        if (!data || !selectedParticipantKey) return null;
        return (
            data.participants.find(
                (p) => `${p.user_id}:${getParticipantTeamId(p)}` === selectedParticipantKey,
            ) ?? null
        );
    }, [data, selectedParticipantKey]);

    const attemptsBar = useMemo(() => {
        // Sum attempts per participant (user_id+role_id).
        const agg = new Map<string, number>();
        for (const r of deliveryRows) {
            const key = `${r.user_id}:${r.role_id}`;
            agg.set(key, (agg.get(key) || 0) + Number(r.attempt_count || 0));
        }
        const entries = Array.from(agg.entries()).map(([key, totalAttempts]) => {
            const [userId, roleId] = key.split(':');
            const label = participantKeyToName.get(key) || `User ${userId}`;
            const shortLabel = label.length > 22 ? `${label.slice(0, 22)}...` : label;
            return {label, shortLabel, totalAttempts};
        });
        entries.sort((a, b) => b.totalAttempts - a.totalAttempts);
        const top = entries.slice(0, 10);
        return {
            labels: top.map((e) => e.shortLabel),
            values: top.map((e) => e.totalAttempts),
            full: entries,
        };
    }, [deliveryRows, participantKeyToName]);

    const attemptStats = useMemo(() => {
        const rows = attemptsBar.full ?? [];
        const totals = rows.map((r) => Number(r.totalAttempts || 0));
        const totalAttempts = totals.reduce((acc, x) => acc + x, 0);
        const activeParticipants = totals.filter((x) => x > 0).length;
        const participantCount = totals.length;
        const avgAttempts = participantCount > 0 ? Number((totalAttempts / participantCount).toFixed(2)) : 0;
        const sorted = [...totals].sort((a, b) => a - b);
        const medianAttempts =
            sorted.length === 0
                ? 0
                : sorted.length % 2 === 1
                    ? sorted[(sorted.length - 1) / 2]
                    : Number((((sorted[sorted.length / 2 - 1] ?? 0) + (sorted[sorted.length / 2] ?? 0)) / 2).toFixed(2));
        const topPerformer = rows[0] ?? null;
        return {
            totalAttempts,
            activeParticipants,
            avgAttempts,
            medianAttempts,
            topPerformer,
        };
    }, [attemptsBar]);
    const attemptChartGroups = useMemo(() => {
        const rows = attemptsBar.full ?? [];
        const chunkSize = 5;
        const groups: Array<{ title: string; labels: string[]; values: number[]; chartWidth: number }> = [];
        for (let i = 0; i < rows.length; i += chunkSize) {
            const chunk = rows.slice(i, i + chunkSize);
            const from = i + 1;
            const to = i + chunk.length;
            groups.push({
                title: `Rank #${from}-${to}`,
                labels: chunk.map((r) => r.shortLabel),
                values: chunk.map((r) => r.totalAttempts),
                chartWidth: Math.max(380, chunk.length * 120),
            });
        }
        return groups;
    }, [attemptsBar]);

    const correctnessPie = useMemo((): PieDatum[] => {
        let correct = 0;
        let incorrect = 0;
        for (const r of deliveryRows) {
            if ((r.attempt_count ?? 0) > 0) {
                if (r.is_correct) correct += 1;
                else incorrect += 1;
            }
        }
        return [
            {label: 'Correct', value: correct, color: '#16a34a'},
            {label: 'Incorrect', value: incorrect, color: '#ef4444'},
        ];
    }, [deliveryRows]);

    const hintsPerDayBar = useMemo(() => {
        // Count all hint timestamps per day.
        const dayMap = new Map<string, number>();
        for (const d of data?.inject_deliveries ?? []) {
            const hintTsList = d.hint_used_at_list ?? [];
            if (hintTsList.length === 0 && (d.hint_used ?? false)) {
                const fallbackTs = d.responded_at || d.sent_at;
                if (fallbackTs) {
                    hintTsList.push(fallbackTs);
                }
            }
            for (const ts of hintTsList ?? []) {
                if (!ts) continue;
                const date = new Date(ts);
                // Skip invalid dates.
                if (Number.isNaN(date.getTime())) continue;
                const key = date.toISOString().slice(0, 10); // YYYY-MM-DD (UTC)
                dayMap.set(key, (dayMap.get(key) || 0) + 1);
            }
        }
        const entries = Array.from(dayMap.entries())
            .map(([day, count]) => ({day, count}))
            .sort((a, b) => a.day.localeCompare(b.day));
        return {
            labels: entries.map((e) => e.day.slice(5)), // MM-DD
            values: entries.map((e) => e.count),
        };
    }, [data]);

    const exportToExcel = async () => {
        if (!data) return;
        // Keep the Excel rows aligned with the UI table columns.
        // Since the UI is grouped by phase, we duplicate a row per phase an inject belongs to.
        const sheetRows: any[] = [];
        for (const r of deliveryRows) {
            const selectedOptionIds = Array.isArray((r as { selected_option_ids?: Array<number | string> }).selected_option_ids)
                ? (r as { selected_option_ids?: Array<number | string> }).selected_option_ids ?? []
                : [];
            const selectedOptionIdsText = selectedOptionIds.length ? selectedOptionIds.join(', ') : '';
            const responseText = String(r.response_text ?? '').trim();
            const answerValue = responseText || selectedOptionIdsText || '—';
            const phases = r.phase_names?.length ? r.phase_names : ['Unassigned'];
            for (const ph of phases) {
                sheetRows.push({
                    'Phase': ph,
                    'Inject Title': r.inject_title,
                    'Inject ID': r.inject_id,
                    'Inject Points Type': r.inject_points_type,
                    'User ID': r.user_id,
                    'Participant': r.participant_name,
                    'Team ID': r.team.id,
                    'Team Name': r.team.name,
                    'Attempts': r.attempt_count,
                    'Is Correct': r.attempt_count > 0 ? (r.is_correct ? 'Yes' : 'No') : '—',
                    'Hint Used': r.hint_used ? 'Yes' : 'No',
                    'Hint Used At (list)': r.hint_used_at_list,
                    'Sent At': r.sent_at,
                    'Responded At': r.responded_at,
                    'Obtained Score': r.obtained_score,
                    'Bonus Points': r.bonus_points,
                    'Response Text': r.response_text,
                    'Selected Option IDs': selectedOptionIdsText,
                    'Answer (Text or Options)': answerValue,
                    'Remarks': r.remarks,
                    'Evaluated By ID': r.evaluated_by_id,
                });
            }
        }

        const ws = XLSX.utils.json_to_sheet(sheetRows, {skipHeader: false});
        const wb = XLSX.utils.book_new();
        const fileBase = data?.uuid ? data.uuid : 'exercise-archive';
        XLSX.utils.book_append_sheet(wb, ws, 'Report');
        XLSX.writeFile(wb, `exercise-archive-${fileBase}-report.xlsx`);
    };

    const exportFeedbackToExcel = () => {
        if (!data) return;
        const rows: Array<Record<string, unknown>> = [];
        const feedbackItems = data.feedback ?? [];

        for (const fb of feedbackItems) {
            const participant = data.participants.find((p) => p.id === fb.participant_id);
            const teamId = participant?.team?.id ?? '';
            const participantName =
                participantKeyToPersonName.get(`${fb.participant_user_id}:${teamId}`) ||
                (fb.participant_user_id ? `User ${fb.participant_user_id}` : '—');
            const submittedBy = fb.submitted_by_name || (fb.submitted_by_id ? `User ${fb.submitted_by_id}` : '—');
            const submittedAt = fb.created_at ? format(new Date(fb.created_at), 'PP p') : '—';

            const payload = fb.payload ?? {};
            for (const [sectionKey, sectionValue] of Object.entries(payload)) {
                const section = sectionKey.replaceAll('_', ' ');
                if (!Array.isArray(sectionValue)) {
                    rows.push({
                        Participant: participantName,
                        'Submitted By': submittedBy,
                        'Submitted At': submittedAt,
                        Section: section,
                        Question: section,
                        Answer: renderFeedbackAnswer(sectionValue),
                    });
                    continue;
                }
                if (sectionValue.length === 0) {
                    rows.push({
                        Participant: participantName,
                        'Submitted By': submittedBy,
                        'Submitted At': submittedAt,
                        Section: section,
                        Question: '',
                        Answer: '',
                    });
                    continue;
                }
                for (let idx = 0; idx < sectionValue.length; idx += 1) {
                    const item = sectionValue[idx];
                    if (!isQuestionAnswerItem(item)) {
                        rows.push({
                            Participant: participantName,
                            'Submitted By': submittedBy,
                            'Submitted At': submittedAt,
                            Section: section,
                            Question: `Item ${idx + 1}`,
                            Answer: renderFeedbackAnswer(item),
                        });
                        continue;
                    }
                    rows.push({
                        Participant: participantName,
                        'Submitted By': submittedBy,
                        'Submitted At': submittedAt,
                        Section: section,
                        Question: item.question || item.id || `Item ${idx + 1}`,
                        Answer: renderFeedbackAnswer(item.answer),
                    });
                }
            }
        }

        const ws = XLSX.utils.json_to_sheet(rows, {skipHeader: false});
        const wb = XLSX.utils.book_new();
        const fileBase = data?.uuid ? data.uuid : 'exercise-archive';
        XLSX.utils.book_append_sheet(wb, ws, 'Feedback');
        XLSX.writeFile(wb, `exercise-archive-${fileBase}-feedback.xlsx`);
    };

    const exportFlagsToExcel = () => {
        if (!data) return;
        const rows: Array<Record<string, unknown>> = [];
        for (const fm of flagMappings as any[]) {
            const flagId = String(fm?.flag?.id ?? fm?.flag_id ?? fm?.flagId ?? '').trim() || '—';
            const usage = flagUsageByFlagId.get(flagId);
            const submissionBlocks = participantFlagSubmissions
                .filter((r) => String(r.flagId ?? '').trim() === flagId)
                .map((r) => {
                    const attempts = (r.submittedResponses ?? [])
                        .map((a, idx) => {
                            const txt = String(a?.submitted_response ?? '').trim() || '—';
                            const corr = a?.is_correct ? 'Correct' : 'Incorrect';
                            const at =
                                a?.created_at != null && a.created_at !== ''
                                    ? format(new Date(a.created_at), 'PP p')
                                    : '—';
                            return `${idx + 1}) ${txt} [${corr}] @ ${at}`;
                        })
                        .join(' | ');
                    return `${r.participantName}: ${attempts || '(no submitted responses)'}`;
                })
                .join('\n');
            const question = String(fm?.flag?.question ?? fm?.flag_question ?? '').trim() || '—';
            const teamName =
                String(
                    fm?.flag?.team_name ??
                        fm?.flag?.teamName ??
                        fm?.exercise_team_name ??
                        fm?.team_name ??
                        '',
                ).trim() || '—';
            const phaseName = String(fm?.flag?.phase_name ?? fm?.flag?.phaseName ?? fm?.phase_name ?? '').trim() || '—';
            const score = Number(fm?.flag?.score ?? fm?.flag_score ?? 0) || 0;
            const scoreType = String(fm?.flag?.score_type ?? fm?.flag?.scoreType ?? fm?.score_type ?? '').trim() || '—';
            const hint = String(fm?.flag?.hint ?? fm?.flag_hint ?? '').trim() || '—';
            const hintPenalty =
                fm?.flag?.hint_penalty !== undefined && fm?.flag?.hint_penalty !== null
                    ? String(fm?.flag?.hint_penalty)
                    : fm?.flag_hint_penalty != null
                      ? String(fm?.flag_hint_penalty)
                      : '—';

            rows.push({
                'Flag ID': flagId,
                Question: question,
                Team: teamName,
                Phase: phaseName,
                Score: score,
                'Score Type': scoreType,
                Hint: hint,
                'Hint Penalty': hintPenalty,
                Used: usage ? 'Yes' : 'No',
                Attempts: usage?.attempts ?? 0,
                'Solved (participants)': usage?.solvedCount ?? 0,
                'Hint Used (participants)': usage?.hintUsedCount ?? 0,
                'Participants (mapped)': usage?.participantCount ?? 0,
                'Submitted Responses': submissionBlocks || '—',
            });
        }

        const ws = XLSX.utils.json_to_sheet(rows, { skipHeader: false });
        const wb = XLSX.utils.book_new();
        const fileBase = data?.uuid ? data.uuid : 'exercise-archive';
        XLSX.utils.book_append_sheet(wb, ws, 'Flags');
        XLSX.writeFile(wb, `exercise-archive-${fileBase}-flags.xlsx`);
    };

    const exportFlagSubmissionsToExcel = () => {
        if (!data) return;
        const detailRows: Array<Record<string, unknown>> = [];

        for (const row of participantFlagSubmissions) {
            if (row.submittedResponses.length === 0) {
                detailRows.push({
                    Participant: row.participantName,
                    Team: row.teamName,
                    'Flag ID': row.flagId,
                    Phase: row.phaseName,
                    Question: row.question,
                    Score: row.score,
                    Solved: row.isSolved ? 'Yes' : 'No',
                    'Hint Used': row.isHintUsed ? 'Yes' : 'No',
                    Attempt: '',
                    'Submitted Response': '',
                    Correct: '',
                    'Submitted At': '',
                });
                continue;
            }
            row.submittedResponses.forEach((attempt, idx) => {
                detailRows.push({
                    Participant: row.participantName,
                    Team: row.teamName,
                    'Flag ID': row.flagId,
                    Phase: row.phaseName,
                    Question: row.question,
                    Score: row.score,
                    Solved: row.isSolved ? 'Yes' : 'No',
                    'Hint Used': row.isHintUsed ? 'Yes' : 'No',
                    Attempt: idx + 1,
                    'Submitted Response': attempt?.submitted_response ?? '',
                    Correct: attempt?.is_correct ? 'Yes' : 'No',
                    'Submitted At': attempt?.created_at
                        ? format(new Date(attempt.created_at), 'PP p')
                        : '',
                });
            });
        }

        /** One row per participant: all submitted response text grouped by flag (phase + question). */
        const byParticipantRows: Array<Record<string, unknown>> = [];
        for (const p of data.participants ?? []) {
            const participantName = p.full_name || p.email || `User ${p.user_id}`;
            const teamName = teamIdToName.get(getParticipantTeamId(p)) || p.team?.name || '—';
            const flagsRaw = (p as ImmutableExerciseReportParticipant & {flags?: unknown}).flags;
            const flags = Array.isArray(flagsRaw)
                ? (flagsRaw as Array<{
                      phase_name?: string;
                      question?: string;
                      flag_id?: string;
                      is_solved?: boolean;
                      submitted_responses?: Array<{
                          submitted_response?: string;
                          is_correct?: boolean;
                          created_at?: string | null;
                      }>;
                  }>)
                : [];
            const blocks: string[] = [];
            let totalAttempts = 0;
            let solvedCount = 0;
            for (const f of flags) {
                if (f?.is_solved) solvedCount++;
                const responses = Array.isArray(f?.submitted_responses) ? f.submitted_responses : [];
                totalAttempts += responses.length;
                const phaseName = String(f?.phase_name ?? '—');
                const question = String(f?.question || f?.flag_id || '—');
                const header = `Phase: ${phaseName} | Flag: ${question}`;
                if (responses.length === 0) {
                    blocks.push(`${header}\n  (no submitted responses)`);
                    continue;
                }
                const lines = responses.map((a, idx) => {
                    const txt = String(a?.submitted_response ?? '').trim() || '—';
                    const corr = a?.is_correct ? 'Yes' : 'No';
                    const at =
                        a?.created_at != null && a.created_at !== ''
                            ? format(new Date(a.created_at), 'PP p')
                            : '—';
                    return `  ${idx + 1}. ${txt} | Correct: ${corr} | ${at}`;
                });
                blocks.push(`${header}\n${lines.join('\n')}`);
            }
            byParticipantRows.push({
                Participant: participantName,
                Team: teamName,
                'User ID': p.user_id,
                Email: p.email ?? '',
                'Total Flags': flags.length,
                'Solved Flags': solvedCount,
                'Total Attempts': totalAttempts,
                'Submitted Responses (by participant)': blocks.join('\n\n'),
            });
        }

        const wb = XLSX.utils.book_new();
        const fileBase = data?.uuid ? data.uuid : 'exercise-archive';
        const wsByParticipant = XLSX.utils.json_to_sheet(byParticipantRows, {skipHeader: false});
        const wsDetail = XLSX.utils.json_to_sheet(detailRows, {skipHeader: false});
        XLSX.utils.book_append_sheet(wb, wsByParticipant, 'Submitted by participant');
        XLSX.utils.book_append_sheet(wb, wsDetail, 'Flag Submissions');
        XLSX.writeFile(wb, `exercise-archive-${fileBase}-flag-submissions.xlsx`);
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!archiveUuid) {
                setError('Missing archive id');
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const res = await v2Api.dashboard.exerciseArchives.immutableReport(archiveUuid);
                if (!mounted) return;
                setData(res);
            } catch (e) {
                if (!mounted) return;
                setError(e instanceof Error ? e.message : 'Failed to load immutable report');
                setData(null);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [archiveUuid]);

    const flagCount = useMemo(() => {
        const fm = data?.flags;
        if (Array.isArray(fm)) return fm.length;
        const fallback = (scenarioSnapshot as any)?.flag_mappings;
        return Array.isArray(fallback) ? fallback.length : 0;
    }, [data, scenarioSnapshot]);

    const flagMappings = useMemo(() => {
        const apiFlags = data?.flags;
        if (Array.isArray(apiFlags)) return apiFlags;
        const fm = (scenarioSnapshot as { flag_mappings?: unknown[] }).flag_mappings;
        return Array.isArray(fm) ? fm : [];
    }, [data, scenarioSnapshot]);

    const participantFlagSubmissions = useMemo(() => {
        if (!data) return [];
        const rows: Array<{
            participantName: string;
            teamName: string;
            flagId: string;
            phaseName: string;
            phaseId: number | null;
            question: string;
            score: number;
            isSolved: boolean;
            isHintUsed: boolean;
            submittedResponses: Array<{
                submitted_response?: string;
                is_correct?: boolean;
                created_at?: string | null;
            }>;
        }> = [];
        for (const p of data.participants ?? []) {
            const participantName = p.full_name || p.email || `User ${p.user_id}`;
            const teamName = teamIdToName.get(getParticipantTeamId(p)) || p.team?.name || '—';
            const flags = Array.isArray((p as any).flags) ? ((p as any).flags as any[]) : [];
            for (const f of flags) {
                rows.push({
                    participantName,
                    teamName,
                    flagId: String(f?.flag_id ?? '—'),
                    phaseName: String(f?.phase_name ?? '—'),
                    phaseId: f?.phase_id ?? null,
                    question: String(f?.question || f?.flag_id || '—'),
                    score: Number(f?.score ?? 0) || 0,
                    isSolved: Boolean(f?.is_solved),
                    isHintUsed: Boolean(f?.is_hint_used),
                    submittedResponses: Array.isArray(f?.submitted_responses) ? f.submitted_responses : [],
                });
            }
        }
        return rows;
    }, [data, teamIdToName]);

    const flagUsageByFlagId = useMemo(() => {
        const m = new Map<
            string,
            { attempts: number; solvedCount: number; hintUsedCount: number; participantCount: number }
        >();
        for (const r of participantFlagSubmissions) {
            const id = String(r.flagId ?? '').trim();
            if (!id || id === '—') continue;
            const cur = m.get(id) ?? { attempts: 0, solvedCount: 0, hintUsedCount: 0, participantCount: 0 };
            cur.participantCount += 1;
            cur.attempts += r.submittedResponses?.length ?? 0;
            if (r.isSolved) cur.solvedCount += 1;
            if (r.isHintUsed) cur.hintUsedCount += 1;
            m.set(id, cur);
        }
        return m;
    }, [participantFlagSubmissions]);

    const flagSubmissionSummary = useMemo(() => {
        const total = participantFlagSubmissions.length;
        const solved = participantFlagSubmissions.filter((r) => r.isSolved).length;
        const hintUsed = participantFlagSubmissions.filter((r) => r.isHintUsed).length;
        const attempts = participantFlagSubmissions.reduce(
            (acc, r) => acc + (r.submittedResponses?.length ?? 0),
            0,
        );
        return { total, solved, hintUsed, attempts };
    }, [participantFlagSubmissions]);

    const teamCount = useMemo(() => {
        const tm = (scenarioSnapshot as { team_mappings?: unknown[] }).team_mappings;
        return Array.isArray(tm) ? tm.length : 0;
    }, [scenarioSnapshot]);

    return (
        <div className="space-y-6 mx-auto">
            <div className="flex flex-wrap items-center gap-4">
                <Button variant="ghost" size="sm" asChild>
                    <Link to="/scenarios/reports">
                        <ArrowLeft className="mr-2 h-4 w-4"/>
                        Report history
                    </Link>
                </Button>
                <Badge variant="secondary" className="gap-1">
                    <Shield className="h-3.5 w-3.5"/>
                    Historical report
                </Badge>
            </div>

            {loading && (
                <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
                    <Loader2 className="h-6 w-6 animate-spin"/>
                    Loading report snapshot…
                </div>
            )}

            {!loading && error && (
                <Card className="border-destructive/50">
                    <CardHeader>
                        <CardTitle className="text-destructive">Could not load report</CardTitle>
                        <CardDescription>{error}</CardDescription>
                    </CardHeader>
                </Card>
            )}

            {!loading && data && (
                <>
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
                            <Archive className="h-7 w-7 text-muted-foreground"/>
                            {data.name ?? 'Exercise report'}
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Frozen exercise title at completion:{' '}
                            <span className="font-medium text-foreground">
                {frozenExerciseName((scenarioSnapshot as Record<string, unknown>) ?? {})}
              </span>
                        </p>
                        <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-4 w-4"/>
                Ended {data.end_at ? format(new Date(data.end_at), 'PP') : '—'}
              </span>
                            {(scenarioSnapshot as any)?.exercise?.id && (
                                <span className="font-mono text-xs">Exercise ID: {(scenarioSnapshot as any).exercise.id}</span>
                            )}
                        </div>
                    </div>

                    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
                        <div className="flex gap-6">
                            <aside className="w-56 shrink-0">
                                <TabsList className="flex flex-col items-start w-full h-auto p-2 rounded-md border bg-card">
                                    <TabsTrigger value="overview" className="w-full justify-start">
                                        Overview
                                    </TabsTrigger>
                                    <TabsTrigger value="participants" className="w-full justify-start">
                                        Participants
                                    </TabsTrigger>
                                    <TabsTrigger value="injects" className="w-full justify-start">
                                        Inject deliveries
                                    </TabsTrigger>
                                    <TabsTrigger value="flags" className="w-full justify-start">
                                        Flags
                                    </TabsTrigger>
                                    <TabsTrigger value="feedback" className="w-full justify-start">
                                        Feedback
                                    </TabsTrigger>
                                </TabsList>
                            </aside>

                            <main className="flex-1 space-y-6">
                                {activeTab === 'overview' && (
                                    <div className="space-y-6">
                                        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
                                            <Card>
                                                <CardHeader className="pb-2">
                                                    <CardTitle className="text-sm font-medium text-muted-foreground">Participants</CardTitle>
                                                </CardHeader>
                                                <CardContent className="text-2xl font-semibold flex items-center gap-2">
                                                    <Users className="h-5 w-5 text-muted-foreground"/>
                                                    {summary?.participants_count ?? data.participants.length}
                                                </CardContent>
                                            </Card>
                                            <Card>
                                                <CardHeader className="pb-2">
                                                    <CardTitle className="text-sm font-medium text-muted-foreground">Teams</CardTitle>
                                                </CardHeader>
                                                <CardContent className="text-2xl font-semibold">{summary?.teams_count ?? teamScores.length}</CardContent>
                                            </Card>
                                            <Card>
                                                <CardHeader className="pb-2">
                                                    <CardTitle className="text-sm font-medium text-muted-foreground">Inject deliveries</CardTitle>
                                                </CardHeader>
                                                <CardContent className="text-2xl font-semibold">{data.inject_deliveries.length}</CardContent>
                                            </Card>
                                            <Card>
                                                <CardHeader className="pb-2">
                                                    <CardTitle className="text-sm font-medium text-muted-foreground">Definition</CardTitle>
                                                </CardHeader>
                                                <CardContent className="text-sm text-muted-foreground">
                                                    {teamCount} team mapping{teamCount === 1 ? '' : 's'}, {flagCount} flag mapping
                                                    {flagCount === 1 ? '' : 's'}
                                                </CardContent>
                                            </Card>
                                            <Card>
                                                <CardHeader className="pb-2">
                                                    <CardTitle className="text-sm font-medium text-muted-foreground">TTX</CardTitle>
                                                </CardHeader>
                                                <CardContent className="text-sm font-semibold">
                                                    {formatScoreLine(
                                                        Number(summary?.ttx?.obtained?.obtained_score ?? 0) + Number(summary?.ttx?.obtained?.bonus_points ?? 0),
                                                        Number(summary?.ttx?.out_of ?? 0),
                                                    )} ({Number(summary?.ttx?.per ?? 0)}%)
                                                </CardContent>
                                            </Card>
                                            <Card>
                                                <CardHeader className="pb-2">
                                                    <CardTitle className="text-sm font-medium text-muted-foreground">Scenario</CardTitle>
                                                </CardHeader>
                                                <CardContent className="text-sm font-semibold">
                                                    {formatScoreLine(
                                                        Number(summary?.scenarios?.obtained?.obtained_score ?? 0) + Number(summary?.scenarios?.obtained?.bonus_points ?? 0),
                                                        Number(summary?.scenarios?.out_of ?? 0),
                                                    )} ({Number(summary?.scenarios?.per ?? 0)}%)
                                                </CardContent>
                                            </Card>
                                            <Card>
                                                <CardHeader className="pb-2">
                                                    <CardTitle className="text-sm font-medium text-muted-foreground">Grand Total</CardTitle>
                                                </CardHeader>
                                                <CardContent className="text-sm font-semibold">
                                                    {formatScoreLine(
                                                        Number(summary?.grand_total?.obtained?.obtained_score ?? 0) + Number(summary?.grand_total?.obtained?.bonus_points ?? 0),
                                                        Number(summary?.grand_total?.out_of ?? 0),
                                                    )} ({Number(summary?.grand_total?.per ?? 0)}%)
                                                </CardContent>
                                            </Card>
                                        </div>

                                        <div className="grid gap-4 lg:grid-cols-3">
                                            <Card className="lg:col-span-1">
                                                <CardHeader>
                                                    <CardTitle className="text-sm font-medium text-muted-foreground">Attempts per participant</CardTitle>
                                                    <CardDescription>Ranked by total submissions across all inject deliveries.</CardDescription>
                                                </CardHeader>
                                                <CardContent className="pt-0">
                                                    <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
                                                        <div className="rounded border p-2">
                                                            <div className="text-muted-foreground">Total attempts</div>
                                                            <div className="font-semibold">{attemptStats.totalAttempts}</div>
                                                        </div>
                                                        <div className="rounded border p-2">
                                                            <div className="text-muted-foreground">Active participants</div>
                                                            <div className="font-semibold">{attemptStats.activeParticipants}</div>
                                                        </div>
                                                        <div className="rounded border p-2">
                                                            <div className="text-muted-foreground">Avg attempts</div>
                                                            <div className="font-semibold">{attemptStats.avgAttempts}</div>
                                                        </div>
                                                        <div className="rounded border p-2">
                                                            <div className="text-muted-foreground">Median attempts</div>
                                                            <div className="font-semibold">{attemptStats.medianAttempts}</div>
                                                        </div>
                                                    </div>
                                                    {attemptStats.topPerformer && (
                                                        <div className="mb-4 text-xs text-muted-foreground">
                                                            Top performer: <span className="font-medium text-foreground">{attemptStats.topPerformer.label}</span> ({attemptStats.topPerformer.totalAttempts})
                                                        </div>
                                                    )}
                                                    <div className="space-y-4">
                                                        {attemptChartGroups.map((group) => (
                                                            <div key={group.title}>
                                                                <div className="text-xs text-muted-foreground mb-2">{group.title}</div>
                                                                <div className="overflow-x-auto">
                                                                    <div style={{width: `${group.chartWidth}px`}}>
                                                                        <D3BarChart
                                                                            data={group.values}
                                                                            labels={group.labels}
                                                                            height={340}
                                                                            width={group.chartWidth}
                                                                            showValues
                                                                            showGrid={true}
                                                                            barColor="#3b82f6"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {attemptChartGroups.length === 0 && (
                                                            <div className="text-sm text-muted-foreground">No attempt data available.</div>
                                                        )}
                                                    </div>
                                                </CardContent>
                                            </Card>

                                            <Card className="lg:col-span-1">
                                                <CardHeader>
                                                    <CardTitle className="text-sm font-medium text-muted-foreground">Correctness distribution</CardTitle>
                                                    <CardDescription>Among deliveries with at least 1 attempt.</CardDescription>
                                                </CardHeader>
                                                <CardContent className="pt-0">
                                                    <D3PieChart
                                                        data={correctnessPie}
                                                        width={280}
                                                        height={280}
                                                        innerRadius={58}
                                                        legend={true}
                                                    />
                                                </CardContent>
                                            </Card>

                                            <Card className="lg:col-span-1">
                                                <CardHeader>
                                                    <CardTitle className="text-sm font-medium text-muted-foreground">Hints used timeline</CardTitle>
                                                    <CardDescription>Counts of hint usage timestamps by day.</CardDescription>
                                                </CardHeader>
                                                <CardContent className="pt-0">
                                                    {hintsPerDayBar.values.length > 0 ? (
                                                        <D3BarChart
                                                            data={hintsPerDayBar.values}
                                                            labels={hintsPerDayBar.labels}
                                                            height={260}
                                                            width={520}
                                                            showValues={false}
                                                            showGrid={true}
                                                            barColor="#f59e0b"
                                                        />
                                                    ) : (
                                                        <div className="text-sm text-muted-foreground">No hints usage data available.</div>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </div>

                                        <Card>
                                            <CardHeader>
                                                <CardTitle className="text-sm font-medium text-muted-foreground">Team Performance (%)</CardTitle>
                                                <CardDescription>Grand total percentage by team.</CardDescription>
                                            </CardHeader>
                                            <CardContent className="pt-0">
                                                <D3BarChart
                                                    data={teamPerformanceChart.values}
                                                    labels={teamPerformanceChart.labels}
                                                    height={280}
                                                    width={900}
                                                    showValues
                                                    showGrid={true}
                                                    barColor="#8b5cf6"
                                                />
                                            </CardContent>
                                        </Card>

                                        <Card>
                                            <CardHeader>
                                                <CardTitle>Team Scores</CardTitle>
                                                <CardDescription>
                                                    Team-level aggregate score from participant reports.
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent className="overflow-x-auto">
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead>S.NO</TableHead>
                                                            <TableHead>Team</TableHead>
                                                            <TableHead>TTX Score</TableHead>
                                                            <TableHead>Scenario Score</TableHead>
                                                            <TableHead>Grand Total</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {teamScores.map((t, index) => {
                                                            const ttxObtained =
                                                                Number(t.score?.ttx?.obtained?.obtained_score ?? 0) +
                                                                Number(t.score?.ttx?.obtained?.bonus_points ?? 0);
                                                            const ttxOutOf = Number(t.score?.ttx?.out_of ?? 0);
                                                            const scObtained =
                                                                Number(t.score?.scenarios?.obtained?.obtained_score ?? 0) +
                                                                Number(t.score?.scenarios?.obtained?.bonus_points ?? 0);
                                                            const scOutOf = Number(t.score?.scenarios?.out_of ?? 0);
                                                            return (
                                                                <TableRow key={String(t.team?.id ?? '')}>
                                                                    <TableCell>#{index + 1}</TableCell>
                                                                    <TableCell className="font-medium">{t.team?.name || '—'}</TableCell>
                                                                    <TableCell>{formatScoreLine(ttxObtained, ttxOutOf)}</TableCell>
                                                                    <TableCell>{formatScoreLine(scObtained, scOutOf)}</TableCell>
                                                                    <TableCell>{formatScoreLine(ttxObtained + scObtained, ttxOutOf + scOutOf)}</TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                        {teamScores.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={5} className="text-center text-muted-foreground">
                                                                    No team score data available.
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </CardContent>
                                        </Card>
                                    </div>
                                )}

                                {activeTab === 'participants' && (
                                    <Card>
                                        <CardHeader>
                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                                <div>
                                                    <CardTitle>Participants</CardTitle>
                                                    <CardDescription>
                                                        Names and roles as recorded at archive time (TTX vs Scenario grand totals: Obtained / Out of)
                                                    </CardDescription>
                                                </div>
                                                <ParticipantsTemplateExportButton
                                                    data={data}
                                                    archiveUuid={data?.uuid}
                                                    teamIdToName={teamIdToName}
                                                    participantTtxScoreMap={participantTtxScoreMap}
                                                    participantTtxOutOfMap={participantTtxOutOfMap}
                                                    participantScenarioScoreMap={participantScenarioScoreMap}
                                                    participantScenarioOutOfMap={participantScenarioOutOfMap}
                                                />
                                            </div>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="rounded-md border">
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead>S.NO</TableHead>
                                                            <TableHead>User ID</TableHead>
                                                            <TableHead>Participant</TableHead>
                                                            <TableHead>Team</TableHead>
                                                            <TableHead>TTX Score </TableHead>
                                                            <TableHead>Scenario Score</TableHead>
                                                            <TableHead>Grand Total</TableHead>
                                                            <TableHead>Phase Scores</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {data.participants.map((p, index) => (
                                                            <Fragment key={`participant-${p.id}`}>
                                                                <TableRow>
                                                                    <TableCell>#{index + 1}</TableCell>
                                                                    <TableCell>{p.user_id}</TableCell>
                                                                    <TableCell className="font-medium flex flex-col">
                                                                        <span>{p.full_name || '—'}</span>
                                                                        <small>{p.email || '—'}</small>
                                                                    </TableCell>
                                                                    <TableCell className="text-muted-foreground">
                                                                        {teamIdToName.get(getParticipantTeamId(p)) || p.team?.name || '—'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {(() => {
                                                                            const key = `${p.user_id}:${getParticipantTeamId(p)}`;
                                                                            const fromApi = p.score?.ttx;
                                                                            const obtainedFromApi =
                                                                                Number(fromApi?.obtained?.obtained_score ?? 0) +
                                                                                Number(fromApi?.obtained?.bonus_points ?? 0);
                                                                            const outOfFromApi = Number(fromApi?.out_of ?? 0);
                                                                            const obtainedFallback = participantTtxScoreMap.get(key) ?? 0;
                                                                            const outOfFallback = participantTtxOutOfMap.get(key) ?? 0;
                                                                            return formatScoreLine(
                                                                                fromApi ? obtainedFromApi : obtainedFallback,
                                                                                fromApi ? outOfFromApi : outOfFallback,
                                                                            );
                                                                        })()}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {(() => {
                                                                            const key = `${p.user_id}:${getParticipantTeamId(p)}`;
                                                                            const fromApi = p.score?.scenarios;
                                                                            const obtainedFromApi =
                                                                                Number(fromApi?.obtained?.obtained_score ?? 0) +
                                                                                Number(fromApi?.obtained?.bonus_points ?? 0);
                                                                            const outOfFromApi = Number(fromApi?.out_of ?? 0);
                                                                            const obtainedFallback = participantScenarioScoreMap.get(key) ?? 0;
                                                                            const outOfFallback = participantScenarioOutOfMap.get(key) ?? 0;
                                                                            return formatScoreLine(
                                                                                fromApi ? obtainedFromApi : obtainedFallback,
                                                                                fromApi ? outOfFromApi : outOfFallback,
                                                                            );
                                                                        })()}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {(() => {
                                                                            const key = `${p.user_id}:${getParticipantTeamId(p)}`;
                                                                            const ttxApi = p.score?.ttx;
                                                                            const scApi = p.score?.scenarios;
                                                                            const obtainedTtx = ttxApi
                                                                                ? Number(ttxApi?.obtained?.obtained_score ?? 0) +
                                                                                Number(ttxApi?.obtained?.bonus_points ?? 0)
                                                                                : (participantTtxScoreMap.get(key) ?? 0);
                                                                            const outOfTtx = ttxApi
                                                                                ? Number(ttxApi?.out_of ?? 0)
                                                                                : (participantTtxOutOfMap.get(key) ?? 0);
                                                                            const obtainedSc = scApi
                                                                                ? Number(scApi?.obtained?.obtained_score ?? 0) +
                                                                                Number(scApi?.obtained?.bonus_points ?? 0)
                                                                                : (participantScenarioScoreMap.get(key) ?? 0);
                                                                            const outOfSc = scApi
                                                                                ? Number(scApi?.out_of ?? 0)
                                                                                : (participantScenarioOutOfMap.get(key) ?? 0);
                                                                            const obtained = obtainedTtx + obtainedSc;
                                                                            const outOf = outOfTtx + outOfSc;
                                                                            return formatScoreLine(obtained, outOf);
                                                                        })()}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                setSelectedParticipantKey(`${p.user_id}:${getParticipantTeamId(p)}`);
                                                                                setPhaseDrawerOpen(true);
                                                                            }}
                                                                        >
                                                                            Show by phase
                                                                        </Button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            </Fragment>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                <Dialog open={phaseDrawerOpen} onOpenChange={setPhaseDrawerOpen}>
                                    <DialogContent
                                        className="left-auto right-0 top-0 h-screen max-h-screen w-[580px] max-w-[95vw] translate-x-0 translate-y-0 rounded-none border-l p-0">
                                        <div className="h-full overflow-y-auto p-6">
                                            <DialogHeader>
                                                <DialogTitle>Score by phase</DialogTitle>
                                                <DialogDescription>
                                                    {selectedParticipant
                                                        ? `${selectedParticipant.full_name || selectedParticipant.email || `User ${selectedParticipant.user_id}`}`
                                                        : 'Participant score breakdown'}
                                                </DialogDescription>
                                            </DialogHeader>

                                            {!selectedParticipant ? (
                                                <div className="mt-6 text-sm text-muted-foreground">No participant selected.</div>
                                            ) : (
                                                <div className="mt-6 space-y-6">
                                                    <Card>
                                                        <CardHeader className="pb-3">
                                                            <CardTitle className="text-base">Scenario by phase</CardTitle>
                                                        </CardHeader>
                                                        <CardContent>
                                                            {(selectedParticipant.score?.scenarios?.phase ?? []).length === 0 ? (
                                                                <div className="text-sm text-muted-foreground">No scenario phase scores.</div>
                                                            ) : (
                                                                <div className="space-y-2">
                                                                    {(selectedParticipant.score?.scenarios?.phase ?? []).map((ph, idx) => (
                                                                        <div
                                                                            key={`scenario-phase-${idx}`}
                                                                            className="flex items-center justify-between rounded-md border p-2 text-sm"
                                                                        >
                                                                            <span className="font-medium">{(ph as any)?.name || 'Unassigned'}</span>
                                                                            <span className="text-muted-foreground">{phaseScoreLine(ph as any)}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </CardContent>
                                                    </Card>

                                                    <Card>
                                                        <CardHeader className="pb-3">
                                                            <CardTitle className="text-base">TTX by phase</CardTitle>
                                                        </CardHeader>
                                                        <CardContent>
                                                            {(selectedParticipant.score?.ttx?.phase ?? []).length === 0 ? (
                                                                <div className="text-sm text-muted-foreground">No TTX phase scores.</div>
                                                            ) : (
                                                                <div className="space-y-2">
                                                                    {(selectedParticipant.score?.ttx?.phase ?? []).map((ph, idx) => (
                                                                        <div
                                                                            key={`ttx-phase-${idx}`}
                                                                            className="flex items-center justify-between rounded-md border p-2 text-sm"
                                                                        >
                                                                            <span className="font-medium">{(ph as any)?.name || 'Unassigned'}</span>
                                                                            <span className="text-muted-foreground">{phaseScoreLine(ph as any)}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </CardContent>
                                                    </Card>

                                                    {(() => {
                                                        const ttx = selectedParticipant.score?.ttx;
                                                        const sc = selectedParticipant.score?.scenarios;
                                                        const ttxObtained =
                                                            Number(ttx?.obtained?.obtained_score ?? 0) +
                                                            Number(ttx?.obtained?.bonus_points ?? 0);
                                                        const ttxOutOf = Number(ttx?.out_of ?? 0);
                                                        const ttxPer =
                                                            Number(
                                                                ttx?.per ??
                                                                (ttxOutOf > 0 ? ((ttxObtained / ttxOutOf) * 100).toFixed(2) : 0),
                                                            ) || 0;

                                                        const scObtained =
                                                            Number(sc?.obtained?.obtained_score ?? 0) +
                                                            Number(sc?.obtained?.bonus_points ?? 0);
                                                        const scOutOf = Number(sc?.out_of ?? 0);
                                                        const scPer =
                                                            Number(
                                                                sc?.per ??
                                                                (scOutOf > 0 ? ((scObtained / scOutOf) * 100).toFixed(2) : 0),
                                                            ) || 0;

                                                        const grandObtained = ttxObtained + scObtained;
                                                        const grandOutOf = ttxOutOf + scOutOf;
                                                        const grandPer = grandOutOf > 0 ? Number(((grandObtained / grandOutOf) * 100).toFixed(2)) : 0;

                                                        return (
                                                            <Card>
                                                                <CardHeader className="pb-3">
                                                                    <CardTitle className="text-base">Summary</CardTitle>
                                                                </CardHeader>
                                                                <CardContent className="space-y-2 text-sm">
                                                                    <div className="flex items-center justify-between rounded-md border p-2">
                                                                        <span className="font-medium">Scenario</span>
                                                                        <span className="text-muted-foreground">
                                                                            {scObtained} / {scOutOf} ({scPer}%)
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center justify-between rounded-md border p-2">
                                                                        <span className="font-medium">TTX</span>
                                                                        <span className="text-muted-foreground">
                                                                            {ttxObtained} / {ttxOutOf} ({ttxPer}%)
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center justify-between rounded-md border p-2 bg-muted/40">
                                                                        <span className="font-semibold">Grand Total</span>
                                                                        <span className="font-semibold">
                                                                            {grandObtained} / {grandOutOf} ({grandPer}%)
                                                                        </span>
                                                                    </div>
                                                                </CardContent>
                                                            </Card>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    </DialogContent>
                                </Dialog>

                                {activeTab === 'flags' && (
                                    <div className="space-y-4">
                                        <Card>
                                            <CardHeader>
                                                <div className="flex flex-wrap items-start justify-between gap-3">
                                                    <div>
                                                        <CardTitle>Flags</CardTitle>
                                                <CardDescription>Frozen flag/question definitions (archive time).</CardDescription>
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={exportFlagsToExcel}
                                                        disabled={flagMappings.length === 0}
                                                    >
                                                        Export Flags
                                                    </Button>
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="rounded-md border">
                                                    <Table>
                                                        <TableHeader>
                                                            <TableRow>
                                                                <TableHead>Question</TableHead>
                                                                <TableHead>Team</TableHead>
                                                                <TableHead>Phase</TableHead>
                                                                <TableHead>Score</TableHead>
                                                                <TableHead>Score Type</TableHead>
                                                                <TableHead>Hint</TableHead>
                                                                <TableHead>Hint Penalty</TableHead>
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {flagMappings.length === 0 && (
                                                                <TableRow>
                                                                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                                                                        No flags found in this archive.
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                            {flagMappings.map((fm: any, idx: number) => {
                                                                const q = fm?.flag?.question ?? fm?.flag_question ?? '';
                                                                const teamName =
                                                                    fm?.flag?.team_name ??
                                                                    fm?.flag?.teamName ??
                                                                    fm?.exercise_team_name ??
                                                                    '';
                                                                const phaseName = fm?.flag?.phase_name ?? '';
                                                                const score = Number(fm?.flag?.score ?? 0);
                                                                const scoreType = fm?.flag?.score_type ?? fm?.flag?.scoreType ?? '';
                                                                const hint = fm?.flag?.hint ?? '';
                                                                const hintPenalty =
                                                                    fm?.flag?.hint_penalty !== undefined && fm?.flag?.hint_penalty !== null
                                                                        ? String(fm?.flag?.hint_penalty)
                                                                        : '';

                                                                return (
                                                                    <TableRow key={fm?.flag?.id ?? fm?.flag_id ?? fm?.id ?? idx}>
                                                                        <TableCell className="max-w-[420px]">
                                                                            <div className="font-medium line-clamp-2">
                                                                                {typeof q === 'string' && q ? q : '—'}
                                                                            </div>
                                                                        </TableCell>
                                                                        <TableCell className="max-w-[200px]">{teamName || '—'}</TableCell>
                                                                        <TableCell className="max-w-[200px]">{phaseName || '—'}</TableCell>
                                                                        <TableCell>{Number.isFinite(score) ? score : 0}</TableCell>
                                                                        <TableCell>{scoreType || '—'}</TableCell>
                                                                        <TableCell className="max-w-[260px] text-xs text-muted-foreground">
                                                                            {typeof hint === 'string' && hint.trim() ? hint : '—'}
                                                                        </TableCell>
                                                                        <TableCell>{hintPenalty || '—'}</TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </div>
                                            </CardContent>
                                        </Card>

                                        <Card>
                                            <CardHeader>
                                                <div className="flex flex-wrap items-start justify-between gap-3">
                                                    <div>
                                                        <CardTitle>Flag Submissions</CardTitle>
                                                        <CardDescription>
                                                            Participant submissions with solved status and attempt history.
                                                        </CardDescription>
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={exportFlagSubmissionsToExcel}
                                                        disabled={participantFlagSubmissions.length === 0}
                                                    >
                                                        Export Data
                                                    </Button>
                                                </div>
                                            </CardHeader>
                                            <CardContent className="space-y-3">
                                                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                                    <div className="rounded-md border bg-muted/20 px-3 py-2">
                                                        <div className="text-xs text-muted-foreground">Mapped participant flags</div>
                                                        <div className="text-lg font-semibold">{flagSubmissionSummary.total}</div>
                                                    </div>
                                                    <div className="rounded-md border bg-muted/20 px-3 py-2">
                                                        <div className="text-xs text-muted-foreground">Solved</div>
                                                        <div className="text-lg font-semibold">{flagSubmissionSummary.solved}</div>
                                                    </div>
                                                    <div className="rounded-md border bg-muted/20 px-3 py-2">
                                                        <div className="text-xs text-muted-foreground">Hint used</div>
                                                        <div className="text-lg font-semibold">{flagSubmissionSummary.hintUsed}</div>
                                                    </div>
                                                    <div className="rounded-md border bg-muted/20 px-3 py-2">
                                                        <div className="text-xs text-muted-foreground">Total attempts</div>
                                                        <div className="text-lg font-semibold">{flagSubmissionSummary.attempts}</div>
                                                    </div>
                                                </div>

                                                {participantFlagSubmissions.length === 0 && (
                                                    <p className="text-sm text-muted-foreground">
                                                        No participant flag submissions found in this archive.
                                                    </p>
                                                )}
                                                {participantFlagSubmissions.map((row, idx) => (
                                                    <details key={`flag-sub-${idx}`} className="rounded-md bg-card/40 px-3 py-2">
                                                        <summary className="cursor-pointer list-none">
                                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                                <div>
                                                                    <div className="text-sm font-medium">{row.participantName}</div>
                                                                    <div className="text-xs text-muted-foreground">{row.teamName}</div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <Badge variant={row.isSolved ? 'default' : 'secondary'}>
                                                                        {row.isSolved ? 'Solved' : 'Unsolved'}
                                                                    </Badge>
                                                                    <Badge variant="outline">Score: {row.score}</Badge>
                                                                    <Badge variant="outline">
                                                                        Attempts: {row.submittedResponses.length}
                                                                    </Badge>
                                                                    <Badge variant="outline">
                                                                        Hint: {row.isHintUsed ? 'Used' : 'No'}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                            <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                                                                {row.question}
                                                            </div>
                                                        </summary>

                                                        <div className="mt-2 space-y-2">
                                                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                                Submitted Responses ({row.submittedResponses.length})
                                                            </div>
                                                            {row.submittedResponses.length === 0 ? (
                                                                <div className="text-xs text-muted-foreground">
                                                                    No submission attempts.
                                                                </div>
                                                            ) : (
                                                                <ul className="space-y-1.5">
                                                                    {row.submittedResponses.map((attempt, i) => (
                                                                        <li
                                                                            key={`attempt-${idx}-${i}`}
                                                                            className="rounded bg-muted/20 px-2 py-1.5 text-xs flex flex-wrap items-center gap-2"
                                                                        >
                                                                            <Badge variant="outline">#{i + 1}</Badge>
                                                                            <span className="font-medium break-words">
                                                                                {attempt?.submitted_response || '—'}
                                                                            </span>
                                                                            <Badge variant={attempt?.is_correct ? 'default' : 'secondary'}>
                                                                                {attempt?.is_correct ? 'Correct' : 'Incorrect'}
                                                                            </Badge>
                                                                            <span className="text-muted-foreground">
                                                                                {attempt?.created_at
                                                                                    ? format(new Date(attempt.created_at), 'PP p')
                                                                                    : '—'}
                                                                            </span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    </details>
                                                ))}
                                            </CardContent>
                                        </Card>
                                    </div>
                                )}

                                {activeTab === 'injects' && (
                                    <Card>
                                        <CardHeader>
                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                                <div>
                                                    <CardTitle>Inject deliveries</CardTitle>
                                                    <CardDescription>
                                                        Titles, max points, and scores are stored in the archive — not loaded from current inject definitions.
                                                    </CardDescription>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={exportToExcel}
                                                    disabled={(data.inject_deliveries ?? []).length === 0}
                                                >
                                                    Export Injects
                                                </Button>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="overflow-x-auto">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>S.NO</TableHead>
                                                        <TableHead>Inject</TableHead>
                                                        <TableHead>Participant</TableHead>
                                                        <TableHead>Phases</TableHead>
                                                        <TableHead>Max pts</TableHead>
                                                        <TableHead>Score</TableHead>
                                                        <TableHead>Sent</TableHead>
                                                        <TableHead>Responded At</TableHead>
                                                        <TableHead>Remarks</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {data.inject_deliveries.map((d, index) => (
                                                        <TableRow key={d.id}>
                                                            <TableCell>#{index + 1}</TableCell>
                                                            <TableCell>
                                                                <div className="font-medium">{d.inject_title || `Inject #${d.inject_id}`}</div>
                                                                <div className="text-xs text-muted-foreground">{d.inject_points_type || '—'}</div>
                                                            </TableCell>
                                                            <TableCell className="text-sm">
                                                                <div className="font-medium">
                                                                    {participantKeyToPersonName.get(`${d.user_id}:${d.role_id}`) || '—'}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    {teamIdToName.get(d.role_id) || '—'}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="max-w-[180px] text-xs text-muted-foreground">
                                                                {(d.inject_phases_snapshot ?? [])
                                                                    .map((x) => x?.name)
                                                                    .filter(Boolean)
                                                                    .join(', ') || '—'}
                                                            </TableCell>
                                                            <TableCell>{d.inject_points}</TableCell>
                                                            <TableCell>
                                                                {d.obtained_score}
                                                                {d.bonus_points ? ` +${d.bonus_points}` : ''}
                                                            </TableCell>
                                                            <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                                                                {d.sent_at ? format(new Date(d.sent_at), 'PP p') : '—'}
                                                            </TableCell>
                                                            <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                                                                {d.responded_at ? format(new Date(d.responded_at), 'PP p') : '—'}
                                                            </TableCell>
                                                            <TableCell className="max-w-[320px] whitespace-nowrap overflow-hidden text-ellipsis">
                                                                {d.remarks || '—'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                            {data.inject_deliveries.length === 0 && (
                                                <p className="text-sm text-muted-foreground py-6 text-center">No inject deliveries in this archive.</p>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}

                                {activeTab === 'feedback' && (
                                    <Card>
                                        <CardHeader>
                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                                <div>
                                                    <CardTitle>Feedback</CardTitle>
                                                    <CardDescription>
                                                        Structured participant feedback captured in archive snapshot.
                                                    </CardDescription>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={exportFeedbackToExcel}
                                                    disabled={(data.feedback ?? []).length === 0}
                                                >
                                                    Export Feedback
                                                </Button>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            {(data.feedback ?? []).map((fb, index) => {
                                                const teamId =
                                                    data.participants.find((p) => p.id === fb.participant_id)?.team?.id ?? '';
                                                const participantDisplay =
                                                    participantKeyToPersonName.get(`${fb.participant_user_id}:${teamId}`) ||
                                                    (fb.participant_user_id ? `User ${fb.participant_user_id}` : '—');
                                                return (
                                                    <details key={fb.id} className="rounded-lg bg-card/40 px-4 py-3" open={index === 0}>
                                                        <summary className="cursor-pointer list-none">
                                                            <div className="flex flex-wrap items-center justify-between gap-3 pb-1">
                                                                <div className="space-y-0.5">
                                                                    <div className="text-sm font-semibold">
                                                                        #{index + 1} {participantDisplay}
                                                                    </div>
                                                                    <div className="text-xs text-muted-foreground">
                                                                        Submitted by {fb.submitted_by_name || (fb.submitted_by_id ? `User ${fb.submitted_by_id}` : '—')}
                                                                    </div>
                                                                </div>
                                                                <Badge variant="outline" className="text-xs">
                                                                    {fb.created_at ? format(new Date(fb.created_at), 'PP p') : '—'}
                                                                </Badge>
                                                            </div>
                                                        </summary>

                                                        <div className="mt-2 space-y-2">
                                                            {Object.entries(fb.payload ?? {}).map(([sectionKey, sectionValue]) => {
                                                                const sectionTitle = sectionKey.replaceAll('_', ' ');
                                                                if (!Array.isArray(sectionValue)) {
                                                                    return (
                                                                        <div key={sectionKey} className="rounded-md bg-muted/20 px-3 py-2">
                                                                            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                                                                                {sectionTitle}
                                                                            </div>
                                                                            <div className="text-sm text-foreground/90 break-words">
                                                                                {renderFeedbackAnswer(sectionValue)}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                }

                                                                const isQuestionsSection = sectionKey === 'questions';
                                                                return (
                                                                    <div key={sectionKey} className="rounded-md bg-muted/20 px-3 py-2">
                                                                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                                                            {sectionTitle} ({sectionValue.length})
                                                                        </div>
                                                                        <div className="space-y-1.5">
                                                                            {sectionValue.length === 0 && (
                                                                                <div className="text-xs text-muted-foreground">No entries.</div>
                                                                            )}
                                                                            {sectionValue.map((item, idx) => {
                                                                                if (!isQuestionAnswerItem(item)) {
                                                                                    return (
                                                                                        <div key={`${sectionKey}-${idx}`} className="rounded bg-background/40 px-2 py-1.5 text-xs break-words">
                                                                                            {renderFeedbackAnswer(item)}
                                                                                        </div>
                                                                                    );
                                                                                }
                                                                                return (
                                                                                    <div key={`${sectionKey}-${item.id ?? idx}`} className="rounded bg-background/40 px-2.5 py-2">
                                                                                        <div className="text-sm font-medium leading-5">
                                                                                            {item.question || item.id || `Item ${idx + 1}`}
                                                                                        </div>
                                                                                        <div className="mt-1 text-xs text-muted-foreground flex items-center gap-2">
                                                                                            <span>Answer:</span>
                                                                                            {isQuestionsSection ? (
                                                                                                <Badge variant="secondary" className="text-[11px] px-1.5 py-0">
                                                                                                    {renderFeedbackAnswer(item.answer)}
                                                                                                </Badge>
                                                                                            ) : (
                                                                                                <span className="break-words">{renderFeedbackAnswer(item.answer)}</span>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </details>
                                                );
                                            })}
                                            {(data.feedback ?? []).length === 0 && (
                                                <p className="text-sm text-muted-foreground py-8 text-center">
                                                    No feedback found in this archive.
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}

                            </main>
                        </div>
                    </Tabs>
                </>
            )}
        </div>
    );
}
